<!-- DeliveryAssistanceCard.vue -->
<template>
  <div class="delivery-tile action-card">
    <div class="delivery-tile-content">
      <div class="action-card-header">
        <h4 class="action-card-title">Actions rapides</h4>
      </div>
      <div class="delivery-tile-body">
        <div class="actions-grid">
          <!-- Navigation Button -->
          <button class="action-button navigation-button" @click="openNavigation" type="button">
            <span class="material-icons">navigation</span>
            <span class="button-text">Y Aller</span>
          </button>

          <!-- Comment Button -->
          <button class="action-button comment-button" @click="openCommentModal" type="button">
            <span class="material-icons">comment</span>
            <span class="button-text">Commentaire</span>
          </button>
          
          <!-- Support Button -->
          <button class="action-button support-button" @click="callSupport" type="button">
            <span class="material-icons">support_agent</span>
            <span class="button-text">Astreinte</span>
          </button>
          
          <!-- Call Client Button -->
          <button class="action-button client-button" @click="callClient" type="button">
            <span class="material-icons">phone</span>
            <span class="button-text">Client</span>
          </button>
          
          
        </div>
      </div>
    </div>
  </div>
  
  <!-- Comment Modal -->
  <Dialog 
    v-model:visible="commentModalVisible" 
    modal 
    header="Ajouter un commentaire" 
    :style="{ width: '90%', maxWidth: '450px' }"
    :closable="true"
  >
    <div class="comment-modal-content">
      <span class="p-float-label">
        <Textarea 
          id="comment" 
          v-model="commentText" 
          rows="5" 
          class="w-full" 
          :autoResize="true" 
          placeholder="Saisissez ici les informations à remonter (ex: nouveau code de porte, etc.)"
        />
      </span>
      <div class="comment-modal-actions">
        <Button 
          label="Annuler" 
          icon="pi pi-times" 
          class="p-button-text" 
          @click="closeCommentModal" 
        />
        <Button 
          label="Envoyer" 
          icon="pi pi-check" 
          class="p-button-primary" 
          @click="submitComment" 
          :loading="submitting"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { defineProps, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import Dialog from 'primevue/dialog'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import axios from '@/api/axios'

const props = defineProps({
  delivery: {
    type: Object,
    required: true
  }
})

const toast = useToast()
const commentModalVisible = ref(false)
const commentText = ref('')
const submitting = ref(false)

const openCommentModal = () => {
  commentModalVisible.value = true
}

const closeCommentModal = () => {
  commentModalVisible.value = false
  commentText.value = ''
}

const submitComment = async () => {
  if (!commentText.value.trim()) {
    toast.add({
      severity: 'warn',
      summary: 'Attention',
      detail: 'Veuillez saisir un commentaire',
      life: 3000
    })
    return
  }
  
  submitting.value = true
  try {
    const response = await axios.post('/deliveryV2/delivery-comments/', {
      delivery: props.delivery.id,
      comment: commentText.value.trim()
    })
    
    toast.add({
      severity: 'success',
      summary: 'Succès',
      detail: 'Votre commentaire a été enregistré',
      life: 3000
    })
    closeCommentModal()
  } catch (error) {
    console.error('Erreur lors de l\'envoi du commentaire:', error)
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Impossible d\'enregistrer le commentaire',
      life: 3000
    })
  } finally {
    submitting.value = false
  }
}

const openNavigation = () => {
  // Coordonnées et adresse
  const fullAddress = `${props.delivery.deliveryLocation.address}, ${props.delivery.deliveryLocation.zip_code} ${props.delivery.deliveryLocation.city}`
  const address = encodeURIComponent(fullAddress)
  const lat = props.delivery.deliveryLocation.latitude
  const lng = props.delivery.deliveryLocation.longitude
  
  // Détection Android ou iOS (pour affiner les URL)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const isAndroid = /Android/.test(navigator.userAgent)
  const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge|Edg/.test(navigator.userAgent)
  
  // Variable pour suivre si une app s'est ouverte
  let appOpened = false
  
  // Essayer d'abord Google Maps (mieux supporté sur Chrome Android)
  const tryGoogleMaps = () => {
    if (lat && lng) {
      if (isAndroid) {
        // URL spécifique à Android
        return `google.navigation:q=${lat},${lng}&mode=d`
      } else {
        // URL universelle 
        return `comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`
      }
    } else if (address) {
      // Fallback sur l'adresse
      return `comgooglemaps://?daddr=${address}&directionsmode=driving`
    }
    return null
  }
  
  // URL pour Waze - format corrigé pour assurer le lancement de l'itinéraire
  const getWazeUrl = () => {
    if (lat && lng) {
      // Format d'URL corrigé pour Waze avec les coordonnées
      if (isAndroid) {
        // Format spécifique pour Android garantissant la navigation
        return `waze://?ll=${lat},${lng}&navigate=yes`
      } else if (isIOS) {
        // Format spécifique pour iOS
        return `waze://?ll=${lat}%2C${lng}&navigate=yes`
      } else {
        // Format générique
        return `waze://?ll=${lat},${lng}&navigate=yes`
      }
    } else if (address) {
      // Pour adresse, on utilise le paramètre "q" qui est plus fiable
      const cleanAddress = encodeURIComponent(fullAddress.replace(/,/g, ' ').replace(/\s+/g, ' ').trim())
      return `waze://?q=${cleanAddress}&navigate=yes`
    }
    return null
  }
  
  // Fonction pour essayer d'ouvrir une application
  const tryOpenApp = (url, appName, isLast = false) => {
    if (!url) return false
    
    // Affichage de debug pour vérifier l'URL (à supprimer en production)
    console.log(`Tentative d'ouverture de ${appName} avec l'URL: ${url}`)
    
    // Sur Chrome pour Android, on utilise un intent pour une meilleure expérience
    if (isAndroid && isChrome && appName === 'Google Maps') {
      window.location.href = url
      return true
    }
    
    try {
      // Utiliser location.assign pour navigation intégrée
      // et éviter les problèmes de blocage de popup
      window.location.assign(url)
      return true
    } catch (e) {
      console.error(`Erreur lors de l'ouverture de ${appName}`, e)
      return false
    }
  }
  
  // Fonction pour fallback sur le web en dernier recours
  const openMapInBrowser = () => {
    // Préférer Google Maps en version web
    toast.add({
      severity: 'info',
      summary: 'Navigation',
      detail: 'Ouverture de Google Maps dans le navigateur',
      life: 3000
    })
    window.location.href = `https://www.google.com/maps/search/?api=1&query=${address}`
  }
  
  // Écouter l'événement blur - indique que l'app a pu s'ouvrir
  const handleAppActivation = () => {
    appOpened = true
    // Nettoyer l'écouteur après usage
    window.removeEventListener('blur', handleAppActivation)
    // En cas de retour, supprimer le timeout
    clearTimeout(fallbackTimerId)
  }
  
  // Ajouter l'écouteur d'événement
  window.addEventListener('blur', handleAppActivation)
  
  // Récupérer les URLs des applications
  const wazeUrl = getWazeUrl()
  const googleMapsUrl = tryGoogleMaps()
  
  // Essayer Waze en premier (puisque c'est votre préférence)
  if (wazeUrl) {
    tryOpenApp(wazeUrl, 'Waze')
  } else if (googleMapsUrl) {
    // Si pas de Waze, essayer Google Maps
    tryOpenApp(googleMapsUrl, 'Google Maps')
  }
  
  // Fallback timer - si aucune app ne s'ouvre après 800ms, on utilise la version web
  const fallbackTimerId = setTimeout(() => {
    if (!appOpened) {
      window.removeEventListener('blur', handleAppActivation)
      openMapInBrowser()
    }
  }, 800)
}

const callSupport = () => {
  // Sur mobile, utiliser le protocole tel:// directement
  // Sur desktop, afficher le numéro à composer
  const phoneNumber = '+33743390976';
  
  // Vérifier si c'est un appareil mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // Utiliser le protocole tel:// qui ouvre l'app téléphone sans quitter la page
    window.location.href = `tel:${phoneNumber}`;
  } else {
    // Sur desktop, afficher un message avec le numéro
    alert(`Veuillez appeler le service d'astreinte au: ${phoneNumber}`);
  }
}

const callClient = () => {
  if (props.delivery.deliveryLocation.telephone) {
    const cleanPhone = props.delivery.deliveryLocation.telephone.replace(/\s+/g, '');
    
    // Vérifier si c'est un appareil mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Utiliser le protocole tel:// qui ouvre l'app téléphone sans quitter la page
      window.location.href = `tel:${cleanPhone}`;
    } else {
      // Sur desktop, afficher un message avec le numéro
      alert(`Veuillez appeler le client au: ${cleanPhone}`);
    }
  } else {
    alert("Numéro de téléphone du client non disponible");
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

/* Styles harmonisés avec MyTournee.vue */
.delivery-tile {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  overflow: hidden;
  width: 100%;
}

.delivery-tile-content {
  display: flex;
  flex-direction: column;
}

.action-card-header {
  padding: 0.8rem 1rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.action-card-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color, #495057);
}

.delivery-tile-body {
  padding: 1rem;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  width: 100%;
}

.action-button {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  border: 1px solid;
  background-color: white;
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.action-button:active {
  transform: translateY(0);
}

/* Styles spécifiques pour chaque type de bouton */
.navigation-button {
  color: var(--primary-color, #3B82F6);
  border-color: var(--primary-color, #3B82F6);
}

.navigation-button:hover {
  background-color: rgba(59, 130, 246, 0.04);
}

.support-button {
  color: #9c27b0;
  border-color: #9c27b0;
}

.support-button:hover {
  background-color: rgba(156, 39, 176, 0.04);
}

.client-button {
  color: #ff9800;
  border-color: #ff9800;
}

.client-button:hover {
  background-color: rgba(255, 152, 0, 0.04);
}

.comment-button {
  color: #4caf50;
  border-color: #4caf50;
}

.comment-button:hover {
  background-color: rgba(76, 175, 80, 0.04);
}

.material-icons {
  font-size: 1.5rem;
}

.button-text {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Styles pour le modal de commentaire */
.comment-modal-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.comment-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}

/* Media queries pour les grands écrans */
@media (min-width: 640px) {
  .action-card-header {
    padding: 1rem;
  }
  
  .actions-grid {
    gap: 1rem;
  }
  
  .action-button {
    padding: 1rem 0.75rem;
  }
  
  .material-icons {
    font-size: 1.75rem;
  }
  
  .button-text {
    font-size: 1rem;
  }
  
  .action-card-title {
    font-size: 1.1rem;
  }
}

/* Media queries pour les petits écrans */
@media (max-width: 480px) {
  .actions-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>